import React, { useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import clsx from 'clsx';

// ── Types ───────────────────────────────────────────────────────

interface QuranicExample {
  surah: number;
  ayah: number;
  surahName: string;
  arabicText: string;           // full ayah text
  highlightWord: string;        // the word(s) containing the rule
  highlightIndices: number[];   // which word indices to highlight (0-based)
  explanation: string;          // what specifically to listen for
}

interface TajweedRuleData {
  id: string;
  name: string;
  arabic: string;
  arabicName: string;           // full Arabic name
  emoji: string;
  tagline: string;              // one-line summary
  description: string;
  howToApply: string[];         // bullet points
  whatToListenFor: string;
  commonMistake: string;
  letters: string;              // relevant letters
  beats: string;                // "2 beats" etc
  color: string;
  colorLight: string;
  colorMuted: string;
  bgColor: string;
  borderColor: string;
  ringColor: string;
  examples: QuranicExample[];
  measurementType: string;      // "duration" | "gap"
  measurementLabel: string;     // what we measure
}

// ── Audio helper ────────────────────────────────────────────────

function buildAudioUrl(surah: number, ayah: number): string {
  const s = String(surah).padStart(3, '0');
  const a = String(ayah).padStart(3, '0');
  return `https://everyayah.com/data/Husary_128kbps/${s}${a}.mp3`;
}

// ── Rule Data ───────────────────────────────────────────────────

const RULES: TajweedRuleData[] = [
  {
    id: 'madd',
    name: 'Madd',
    arabic: 'مَدّ',
    arabicName: 'المَدّ الطبيعي',
    emoji: '〰️',
    tagline: 'Stretch the vowel — hold it for 2 beats',
    description: 'Madd means to prolong a vowel sound. The natural madd (Madd Tabee\'i) lasts 2 beats. It occurs when alef follows fatha, waw with sukoon follows damma, or ya with sukoon follows kasra. Other types of madd (connected, necessary) can extend to 4 or 6 beats.',
    howToApply: [
      'Alef (ا) after a letter with fatha → hold 2 beats',
      'Waw (و) with no vowel after a letter with damma → hold 2 beats',
      'Ya (ي) with no vowel after a letter with kasra → hold 2 beats',
      'Count silently: "one-two" while sustaining the vowel',
    ],
    whatToListenFor: 'A long, smooth vowel sound — like holding a musical note. Not choppy, not cut short.',
    commonMistake: 'Cutting the vowel short (1 beat instead of 2). Beginners tend to rush through madd letters.',
    letters: 'ا و ي',
    beats: '2 beats (Tabee\'i) · 4-6 beats (other types)',
    color: 'text-blue-600 dark:text-blue-400',
    colorLight: 'text-blue-500',
    colorMuted: 'text-blue-400/60 dark:text-blue-500/40',
    bgColor: 'bg-blue-50 dark:bg-blue-500/10',
    borderColor: 'border-blue-200 dark:border-blue-500/25',
    ringColor: 'ring-blue-500/30',
    measurementType: 'duration',
    measurementLabel: 'We measure how long you hold the vowel vs. how long the sheikh holds it',
    examples: [
      {
        surah: 1, ayah: 1, surahName: 'Al-Fatiha',
        arabicText: 'بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ',
        highlightWord: 'الرَّحِيمِ',
        highlightIndices: [3],
        explanation: 'The ya in الرَّحِيمِ after kasra — hold the "ee" sound for 2 beats.',
      },
      {
        surah: 1, ayah: 2, surahName: 'Al-Fatiha',
        arabicText: 'الْحَمْدُ لِلَّهِ رَبِّ الْعَالَمِينَ',
        highlightWord: 'الْعَالَمِينَ',
        highlightIndices: [3],
        explanation: 'The alef in عَا and the ya in مِينَ — both are Madd Tabee\'i.',
      },
      {
        surah: 1, ayah: 7, surahName: 'Al-Fatiha',
        arabicText: 'صِرَاطَ الَّذِينَ أَنْعَمْتَ عَلَيْهِمْ',
        highlightWord: 'صِرَاطَ',
        highlightIndices: [0],
        explanation: 'The alef in صِرَاطَ — hold the "aa" sound for 2 beats.',
      },
    ],
  },
  {
    id: 'ghunnah',
    name: 'Ghunnah',
    arabic: 'غُنَّة',
    arabicName: 'الغُنَّة',
    emoji: '👃',
    tagline: 'Nasal hum through the nose — 2 beats',
    description: 'Ghunnah is a resonant nasal sound that vibrates through the nasal passage for 2 beats. It occurs when noon (ن) or meem (م) carry a shaddah (ّ). The sound should come from the nose, not the mouth — like humming.',
    howToApply: [
      'When you see نّ (noon with shaddah) → hum through your nose for 2 beats',
      'When you see مّ (meem with shaddah) → same nasal hum for 2 beats',
      'Test: pinch your nose — if the sound stops, your ghunnah is correct',
      'The sound should be smooth and sustained, not clipped',
    ],
    whatToListenFor: 'A warm humming sound that resonates in the nose. Like saying "mmm" or "nnn" with your mouth relaxed.',
    commonMistake: 'Rushing through the shaddah without sustaining the nasal sound. Or producing the sound from the throat instead of the nose.',
    letters: 'نّ مّ',
    beats: '2 beats',
    color: 'text-purple-600 dark:text-purple-400',
    colorLight: 'text-purple-500',
    colorMuted: 'text-purple-400/60 dark:text-purple-500/40',
    bgColor: 'bg-purple-50 dark:bg-purple-500/10',
    borderColor: 'border-purple-200 dark:border-purple-500/25',
    ringColor: 'ring-purple-500/30',
    measurementType: 'duration',
    measurementLabel: 'We measure how long you sustain the nasalization vs. the sheikh\'s timing',
    examples: [
      {
        surah: 114, ayah: 1, surahName: 'An-Nas',
        arabicText: 'قُلْ أَعُوذُ بِرَبِّ النَّاسِ',
        highlightWord: 'النَّاسِ',
        highlightIndices: [3],
        explanation: 'The noon with shaddah in النَّاسِ — sustain the nasal "nn" for 2 beats before the alef.',
      },
      {
        surah: 114, ayah: 6, surahName: 'An-Nas',
        arabicText: 'مِنَ الْجِنَّةِ وَالنَّاسِ',
        highlightWord: 'الْجِنَّةِ',
        highlightIndices: [1],
        explanation: 'The noon with shaddah in الْجِنَّةِ — hum through your nose for 2 beats.',
      },
      {
        surah: 1, ayah: 5, surahName: 'Al-Fatiha',
        arabicText: 'إِيَّاكَ نَعْبُدُ وَإِيَّاكَ نَسْتَعِينُ',
        highlightWord: 'إِيَّاكَ',
        highlightIndices: [0, 2],
        explanation: 'The ya with shaddah in إِيَّاكَ — the shaddah doubles the ya sound.',
      },
    ],
  },
  {
    id: 'idgham',
    name: 'Idgham',
    arabic: 'إِدْغَام',
    arabicName: 'الإِدْغَام',
    emoji: '🔗',
    tagline: 'Merge the noon into the next letter — no pause',
    description: 'Idgham means to merge or assimilate. When noon sakinah (نْ) or tanween is followed by one of six letters (ي ر م ل و ن), the noon merges into the following letter with no pause between words. Idgham "with ghunnah" (before ي ن م و) keeps a nasal hum; "without ghunnah" (before ل ر) merges completely.',
    howToApply: [
      'When noon sakinah/tanween is followed by ي ن م و → merge with nasal hum (2 beats)',
      'When noon sakinah/tanween is followed by ل ر → merge completely, no nasal',
      'Key: NO pause between the two words — flow directly from one to the next',
      'Remember the letters with the word: يَرْمُلُونَ (yarmuloon)',
    ],
    whatToListenFor: 'A seamless connection between words — the noon disappears into the next letter. No gap, no separate "n" sound.',
    commonMistake: 'Inserting a pause between the words, or clearly pronouncing the noon before merging. The transition should be instant.',
    letters: 'Before: ي ر م ل و ن',
    beats: '2 beats (with ghunnah) · instant (without)',
    color: 'text-amber-600 dark:text-amber-400',
    colorLight: 'text-amber-500',
    colorMuted: 'text-amber-400/60 dark:text-amber-500/40',
    bgColor: 'bg-amber-50 dark:bg-amber-500/10',
    borderColor: 'border-amber-200 dark:border-amber-500/25',
    ringColor: 'ring-amber-500/30',
    measurementType: 'gap',
    measurementLabel: 'We measure the gap between words — shorter gap = better merging',
    examples: [
      {
        surah: 2, ayah: 8, surahName: 'Al-Baqarah',
        arabicText: 'مِنَ النَّاسِ مَنْ يَقُولُ',
        highlightWord: 'مَنْ يَقُولُ',
        highlightIndices: [2, 3],
        explanation: 'Noon sakinah in مَنْ before ya (ي) → idgham with ghunnah. Merge smoothly: "may-yaqool".',
      },
      {
        surah: 36, ayah: 2, surahName: 'Ya-Sin',
        arabicText: 'وَالْقُرْآنِ الْحَكِيمِ',
        highlightWord: 'الْقُرْآنِ',
        highlightIndices: [0],
        explanation: 'Tanween on آنٍ followed by alef-lam — listen for how the master reciter handles the transition.',
      },
      {
        surah: 112, ayah: 4, surahName: 'Al-Ikhlas',
        arabicText: 'وَلَمْ يَكُنْ لَهُ كُفُوًا أَحَدٌ',
        highlightWord: 'يَكُنْ لَهُ',
        highlightIndices: [2, 3],
        explanation: 'Noon sakinah in يَكُنْ before lam (ل) → idgham without ghunnah. Complete merge, no nasal.',
      },
    ],
  },
  {
    id: 'ikhfa',
    name: 'Ikhfa',
    arabic: 'إِخْفَاء',
    arabicName: 'الإِخْفَاء الحقيقي',
    emoji: '🤫',
    tagline: 'Conceal the noon — a subtle nasal hint before the next letter',
    description: 'Ikhfa means to conceal or hide. When noon sakinah or tanween is followed by one of 15 letters, the noon is partially hidden — not fully pronounced, not fully merged. The result is a light nasal sound that flows into the next letter.',
    howToApply: [
      'The noon sakinah/tanween is not pronounced clearly (that would be izhar)',
      'It\'s not fully merged into the next letter (that would be idgham)',
      'Instead, produce a soft nasal hum that transitions into the next letter',
      'The 15 ikhfa letters: ت ث ج د ذ ز س ش ص ض ط ظ ف ق ك',
    ],
    whatToListenFor: 'A ghostly "n" sound — the noon is present but faint. You can feel the nasality but the noon doesn\'t fully "land" before the next letter arrives.',
    commonMistake: 'Either pronouncing the noon too clearly (making it izhar) or completely dropping it (making it idgham). Ikhfa is the middle ground.',
    letters: 'Before: ت ث ج د ذ ز س ش ص ض ط ظ ف ق ك',
    beats: '2 beats (nasal)',
    color: 'text-orange-600 dark:text-orange-400',
    colorLight: 'text-orange-500',
    colorMuted: 'text-orange-400/60 dark:text-orange-500/40',
    bgColor: 'bg-orange-50 dark:bg-orange-500/10',
    borderColor: 'border-orange-200 dark:border-orange-500/25',
    ringColor: 'ring-orange-500/30',
    measurementType: 'gap',
    measurementLabel: 'We measure the pause between words — a smooth transition means good concealment',
    examples: [
      {
        surah: 2, ayah: 10, surahName: 'Al-Baqarah',
        arabicText: 'وَلَهُمْ عَذَابٌ أَلِيمٌ بِمَا كَانُوا يَكْذِبُونَ',
        highlightWord: 'عَذَابٌ أَلِيمٌ',
        highlightIndices: [2],
        explanation: 'Tanween on عَذَابٌ before hamza — listen for the soft nasal transition.',
      },
      {
        surah: 1, ayah: 7, surahName: 'Al-Fatiha',
        arabicText: 'صِرَاطَ الَّذِينَ أَنْعَمْتَ عَلَيْهِمْ',
        highlightWord: 'أَنْعَمْتَ',
        highlightIndices: [2],
        explanation: 'Noon sakinah in أَنْ before ayn (ع) — the noon is concealed with a nasal hint.',
      },
      {
        surah: 67, ayah: 1, surahName: 'Al-Mulk',
        arabicText: 'تَبَارَكَ الَّذِي بِيَدِهِ الْمُلْكُ وَهُوَ عَلَى كُلِّ شَيْءٍ قَدِيرٌ',
        highlightWord: 'شَيْءٍ قَدِيرٌ',
        highlightIndices: [7, 8],
        explanation: 'Tanween on شَيْءٍ before qaf (ق) — conceal the noon softly before the qaf.',
      },
    ],
  },
  {
    id: 'iqlab',
    name: 'Iqlab',
    arabic: 'إِقْلَاب',
    arabicName: 'الإِقْلَاب',
    emoji: '🔄',
    tagline: 'Convert the noon to a meem sound before ba',
    description: 'Iqlab means to convert or flip. When noon sakinah or tanween is followed by the letter ba (ب), the noon sound transforms into a meem (م) sound. Your lips close to prepare for the ba, and the nasal sound comes through as "meem" instead of "noon".',
    howToApply: [
      'When نْ or tanween is followed by ب → change the noon to a meem sound',
      'Close your lips (as if saying meem) while maintaining a nasal hum',
      'Then proceed directly into the ba',
      'Example: مِنْ بَعْدِ becomes "mim ba\'di" not "min ba\'di"',
    ],
    whatToListenFor: 'The noon transforms into "mmm" right before the ba. Your lips should close before the ba, creating a meem-like bridge.',
    commonMistake: 'Saying a clear "n" before the ba instead of converting to meem. Or pausing between the words.',
    letters: 'Before: ب only',
    beats: '2 beats (nasal)',
    color: 'text-teal-600 dark:text-teal-400',
    colorLight: 'text-teal-500',
    colorMuted: 'text-teal-400/60 dark:text-teal-500/40',
    bgColor: 'bg-teal-50 dark:bg-teal-500/10',
    borderColor: 'border-teal-200 dark:border-teal-500/25',
    ringColor: 'ring-teal-500/30',
    measurementType: 'gap',
    measurementLabel: 'We measure the transition gap and check if the noon converted to meem',
    examples: [
      {
        surah: 2, ayah: 17, surahName: 'Al-Baqarah',
        arabicText: 'ذَهَبَ اللَّهُ بِنُورِهِمْ',
        highlightWord: 'بِنُورِهِمْ',
        highlightIndices: [2],
        explanation: 'Listen to how the sheikh handles noon before ba in real Quranic recitation.',
      },
      {
        surah: 110, ayah: 1, surahName: 'An-Nasr',
        arabicText: 'إِذَا جَاءَ نَصْرُ اللَّهِ وَالْفَتْحُ',
        highlightWord: 'نَصْرُ',
        highlightIndices: [2],
        explanation: 'Listen to the natural flow — no pause between words.',
      },
    ],
  },
  {
    id: 'qalqalah',
    name: 'Qalqalah',
    arabic: 'قَلْقَلَة',
    arabicName: 'القَلْقَلَة',
    emoji: '🔔',
    tagline: 'A bouncing echo on 5 special letters when they have sukoon',
    description: 'Qalqalah is a slight bouncing or vibrating sound produced when one of five letters (ق ط ب ج د) has a sukoon. The letter "bounces" with a small echo rather than being silent. Qalqalah Sughra (minor) occurs mid-word; Qalqalah Kubra (major) occurs at the end of a verse and is more pronounced.',
    howToApply: [
      'The 5 qalqalah letters: ق ط ب ج د — remember: قُطْبُ جَدّ',
      'When any of these has sukoon → add a slight bounce/echo',
      'Don\'t add a vowel (not "qa-la-qa-LA") — just a crisp vibration',
      'At the end of a verse (kubra), the bounce is stronger and clearer',
    ],
    whatToListenFor: 'A short, crisp "pop" or "bounce" after the letter. Not a vowel, not silence — a vibration.',
    commonMistake: 'Swallowing the letter entirely (no bounce) or adding a full vowel sound. The bounce should be subtle and brief.',
    letters: 'ق ط ب ج د (qutb jad)',
    beats: 'Brief echo (not counted in beats)',
    color: 'text-green-600 dark:text-green-400',
    colorLight: 'text-green-500',
    colorMuted: 'text-green-400/60 dark:text-green-500/40',
    bgColor: 'bg-green-50 dark:bg-green-500/10',
    borderColor: 'border-green-200 dark:border-green-500/25',
    ringColor: 'ring-green-500/30',
    measurementType: 'duration',
    measurementLabel: 'We measure word duration — a clipped ending means the bounce is likely missing',
    examples: [
      {
        surah: 112, ayah: 1, surahName: 'Al-Ikhlas',
        arabicText: 'قُلْ هُوَ اللَّهُ أَحَدٌ',
        highlightWord: 'أَحَدٌ',
        highlightIndices: [3],
        explanation: 'The dal at the end of أَحَدٌ — Qalqalah Kubra. The bounce is strong at the end of the verse.',
      },
      {
        surah: 112, ayah: 3, surahName: 'Al-Ikhlas',
        arabicText: 'لَمْ يَلِدْ وَلَمْ يُولَدْ',
        highlightWord: 'يَلِدْ',
        highlightIndices: [1, 3],
        explanation: 'Dal with sukoon in يَلِدْ and يُولَدْ — both get qalqalah. Listen for the bounce on each dal.',
      },
      {
        surah: 113, ayah: 1, surahName: 'Al-Falaq',
        arabicText: 'قُلْ أَعُوذُ بِرَبِّ الْفَلَقِ',
        highlightWord: 'الْفَلَقِ',
        highlightIndices: [3],
        explanation: 'The qaf at the end — bounces with Qalqalah Kubra.',
      },
    ],
  },
];

// ── Audio Player Component ──────────────────────────────────────

const AyahAudioPlayer: React.FC<{
  surah: number;
  ayah: number;
  surahName: string;
  arabicText: string;
  highlightIndices: number[];
  explanation: string;
  ruleColor: string;
  ruleColorLight: string;
}> = ({ surah, ayah, surahName, arabicText, highlightIndices, explanation, ruleColor, ruleColorLight }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [playCount, setPlayCount] = useState(0);

  const togglePlay = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio(buildAudioUrl(surah, ayah));
      audioRef.current.onended = () => setPlaying(false);
      audioRef.current.onerror = () => setPlaying(false);
    }
    if (playing) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    } else {
      audioRef.current.play().then(() => {
        setPlaying(true);
        setPlayCount((c) => c + 1);
      }).catch(() => setPlaying(false));
    }
  }, [playing, surah, ayah]);

  const words = arabicText.split(' ');

  return (
    <div className="rounded-xl bg-white/70 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/50 p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wider">
          {surahName} {surah}:{ayah}
        </span>
        <span className="text-[11px] text-gray-300 dark:text-gray-600">·</span>
        <span className="text-[11px] text-gray-400 dark:text-gray-500">Al-Husary</span>
      </div>

      {/* Arabic text with highlights */}
      <p className="font-arabic text-2xl leading-[2.5] text-center mb-3 select-none" dir="rtl">
        {words.map((word, i) => (
          <span key={i}>
            {i > 0 && ' '}
            <span
              className={clsx(
                'transition-all rounded-sm px-0.5',
                highlightIndices.includes(i)
                  ? `${ruleColor} font-bold underline decoration-2 underline-offset-8 decoration-current/30`
                  : 'text-gray-800 dark:text-gray-200'
              )}
            >
              {word}
            </span>
          </span>
        ))}
      </p>

      {/* Play button + explanation */}
      <div className="flex items-start gap-3">
        <button
          onClick={togglePlay}
          className={clsx(
            'shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all',
            playing
              ? 'bg-amber-500 text-white shadow-md shadow-amber-500/25 scale-105'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
          )}
        >
          {playing ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
          ) : (
            <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
          )}
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-600 dark:text-gray-300">{explanation}</p>
          {playCount > 0 && (
            <p className={clsx('text-xs mt-1', ruleColorLight)}>
              Listened {playCount}x — try to match the sheikh's timing
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Timing Visual ───────────────────────────────────────────────

const TimingVisual: React.FC<{
  label: string;
  beats: number;
  color: string;
}> = ({ label, beats, color }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-gray-500 dark:text-gray-400 w-16 shrink-0">{label}</span>
    <div className="flex gap-1.5">
      {Array.from({ length: beats }).map((_, i) => (
        <motion.div
          key={i}
          className={clsx('w-8 h-3 rounded-full', color)}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: i * 0.3, duration: 0.3, ease: 'easeOut' }}
        />
      ))}
    </div>
    <span className="text-xs text-gray-400 dark:text-gray-500">{beats} beats</span>
  </div>
);

// ── Letters Display ─────────────────────────────────────────────

const LettersDisplay: React.FC<{ letters: string; color: string }> = ({ letters, color }) => (
  <div className="flex flex-wrap gap-2 justify-center">
    {letters.split(' ').filter(Boolean).map((letter, i) => (
      <span
        key={i}
        className={clsx(
          'w-10 h-10 rounded-lg flex items-center justify-center font-arabic text-lg font-bold',
          'bg-white dark:bg-gray-800 border shadow-sm',
          color
        )}
      >
        {letter}
      </span>
    ))}
  </div>
);

// ── Quick Quiz ──────────────────────────────────────────────────

const QUESTIONS_PER_ROUND = 5;

const ALL_QUIZ_QUESTIONS = [
  // ── Iqlab ──
  { arabicText: 'مِنْ بَعْدِ', question: 'What rule applies to the noon sakinah before ba?', options: ['Idgham', 'Ikhfa', 'Iqlab', 'Izhar'], correct: 2, explanation: 'Noon sakinah before ba (ب) → Iqlab! The noon converts to a meem sound.', rule: 'iqlab' },
  { arabicText: 'أَنْبِئْهُمْ', question: 'Noon sakinah followed by ba — which rule?', options: ['Iqlab', 'Ikhfa', 'Idgham', 'Ghunnah'], correct: 0, explanation: 'نْ before ب = Iqlab. Change the noon to a meem sound.', rule: 'iqlab' },
  { arabicText: 'مِنْ بَيْنِ', question: 'The noon before ba here is an example of?', options: ['Madd', 'Iqlab', 'Qalqalah', 'Idgham'], correct: 1, explanation: 'Whenever noon sakinah or tanween meets ba → Iqlab. The only letter that triggers this rule.', rule: 'iqlab' },
  { arabicText: 'سَمِيعٌ بَصِيرٌ', question: 'The tanween on سَمِيعٌ before ba — which rule?', options: ['Ikhfa', 'Iqlab', 'Idgham', 'Izhar'], correct: 1, explanation: 'Tanween before ba = Iqlab. Same rule applies to tanween as noon sakinah.', rule: 'iqlab' },

  // ── Idgham ──
  { arabicText: 'مَنْ يَشَاءُ', question: 'The noon sakinah before ya — which rule?', options: ['Ikhfa', 'Idgham with ghunnah', 'Iqlab', 'Qalqalah'], correct: 1, explanation: 'Ya (ي) is one of the idgham with ghunnah letters (يَنْمُو). The noon merges with a nasal hum.', rule: 'idgham' },
  { arabicText: 'مِنْ رَبِّهِمْ', question: 'Noon sakinah before ra — what happens?', options: ['Idgham without ghunnah', 'Ikhfa', 'Iqlab', 'Idgham with ghunnah'], correct: 0, explanation: 'Ra (ر) triggers idgham WITHOUT ghunnah. The noon merges completely — no nasal.', rule: 'idgham' },
  { arabicText: 'مِنْ لَدُنْهُ', question: 'Noon sakinah before lam — which rule?', options: ['Ghunnah', 'Ikhfa', 'Idgham without ghunnah', 'Madd'], correct: 2, explanation: 'Lam (ل) triggers idgham without ghunnah. Complete merge, no nasal sound.', rule: 'idgham' },
  { arabicText: 'مِنْ وَلِيٍّ', question: 'The noon before waw — which type of idgham?', options: ['Idgham with ghunnah', 'Idgham without ghunnah', 'Ikhfa', 'Iqlab'], correct: 0, explanation: 'Waw (و) is an idgham WITH ghunnah letter. Merge with nasal hum.', rule: 'idgham' },
  { arabicText: 'خَيْرًا نَرَهُ', question: 'Tanween on خَيْرًا before noon — which rule?', options: ['Ikhfa', 'Iqlab', 'Idgham with ghunnah', 'Izhar'], correct: 2, explanation: 'Noon (ن) is an idgham with ghunnah letter. The tanween merges into it.', rule: 'idgham' },

  // ── Ikhfa ──
  { arabicText: 'مِنْ قَبْلُ', question: 'Noon sakinah before qaf — which rule?', options: ['Iqlab', 'Idgham', 'Ikhfa', 'Ghunnah'], correct: 2, explanation: 'Qaf (ق) is one of the 15 ikhfa letters. The noon is concealed with a nasal hint.', rule: 'ikhfa' },
  { arabicText: 'أَنْتَ', question: 'The noon sakinah before ta here is?', options: ['Idgham', 'Ikhfa', 'Iqlab', 'Izhar'], correct: 1, explanation: 'Ta (ت) is an ikhfa letter. The noon is partially hidden — not clear, not merged.', rule: 'ikhfa' },
  { arabicText: 'مِنْ شَرِّ', question: 'Noon sakinah before sheen — which rule?', options: ['Ikhfa', 'Idgham', 'Ghunnah', 'Madd'], correct: 0, explanation: 'Sheen (ش) is one of the 15 ikhfa letters. Conceal the noon softly.', rule: 'ikhfa' },
  { arabicText: 'عَذَابٌ شَدِيدٌ', question: 'Tanween before sheen — what happens?', options: ['Idgham', 'Iqlab', 'Izhar', 'Ikhfa'], correct: 3, explanation: 'Sheen is an ikhfa letter. Tanween is concealed just like noon sakinah.', rule: 'ikhfa' },
  { arabicText: 'يُنْفِقُونَ', question: 'The noon sakinah before fa inside this word?', options: ['Ikhfa', 'Idgham', 'Iqlab', 'Qalqalah'], correct: 0, explanation: 'Fa (ف) is an ikhfa letter. Ikhfa applies even within a single word.', rule: 'ikhfa' },

  // ── Ghunnah ──
  { arabicText: 'إِنَّ اللَّهَ', question: 'The noon with shaddah — which rule?', options: ['Idgham', 'Ghunnah', 'Ikhfa', 'Madd'], correct: 1, explanation: 'Noon with shaddah (نّ) = Ghunnah. Sustain the nasal hum for 2 beats.', rule: 'ghunnah' },
  { arabicText: 'ثُمَّ', question: 'The meem with shaddah — which rule?', options: ['Madd', 'Ikhfa', 'Ghunnah', 'Qalqalah'], correct: 2, explanation: 'Meem with shaddah (مّ) = Ghunnah. Same nasal hum as noon with shaddah.', rule: 'ghunnah' },
  { arabicText: 'الْجَنَّةِ', question: 'What rule is on the noon in الْجَنَّةِ?', options: ['Idgham', 'Ikhfa', 'Ghunnah', 'Iqlab'], correct: 2, explanation: 'The noon has a shaddah (نّ) = Ghunnah. Hum through your nose for 2 beats.', rule: 'ghunnah' },
  { arabicText: 'النَّاسِ', question: 'The noon with shaddah at the start — which rule?', options: ['Ghunnah', 'Madd', 'Idgham', 'Izhar'], correct: 0, explanation: 'نّ = Ghunnah in every position. Let the nasal sound resonate for 2 beats.', rule: 'ghunnah' },

  // ── Madd ──
  { arabicText: 'الرَّحْمَنِ الرَّحِيمِ', question: 'The ya in الرَّحِيمِ after kasra — which rule?', options: ['Ghunnah', 'Qalqalah', 'Madd Tabee\'i', 'Idgham'], correct: 2, explanation: 'Ya with no vowel after kasra = Madd Tabee\'i. Hold for 2 beats.', rule: 'madd' },
  { arabicText: 'قَالُوا', question: 'The alef after fatha on lam — which rule?', options: ['Madd Tabee\'i', 'Ghunnah', 'Idgham', 'Ikhfa'], correct: 0, explanation: 'Alef after fatha = Madd Tabee\'i. Stretch the "aa" sound for 2 beats.', rule: 'madd' },
  { arabicText: 'يَقُولُونَ', question: 'The waw after damma — what type of madd?', options: ['Qalqalah', 'Madd Tabee\'i', 'Ghunnah', 'Ikhfa'], correct: 1, explanation: 'Waw with no vowel after damma = Madd Tabee\'i (waw). Hold the "oo" for 2 beats.', rule: 'madd' },
  { arabicText: 'فِيهَا', question: 'The ya after kasra on fa — which rule?', options: ['Idgham', 'Madd', 'Ikhfa', 'Qalqalah'], correct: 1, explanation: 'Ya after kasra = Madd Tabee\'i. The "ee" sound is held for 2 beats.', rule: 'madd' },
  { arabicText: 'الْعَالَمِينَ', question: 'How many madd letters are in this word?', options: ['0', '1', '2', '3'], correct: 2, explanation: 'Two madd letters: the alef in عَا (after fatha) and the ya in مِي (after kasra).', rule: 'madd' },

  // ── Qalqalah ──
  { arabicText: 'أَحَدٌ', question: 'What happens to the dal at the end?', options: ['Madd', 'Ghunnah', 'Qalqalah Kubra', 'Ikhfa'], correct: 2, explanation: 'Dal (د) is a qalqalah letter. At the end of a verse = Qalqalah Kubra (strong bounce).', rule: 'qalqalah' },
  { arabicText: 'يَلِدْ', question: 'The dal with sukoon — which rule?', options: ['Ghunnah', 'Qalqalah Sughra', 'Madd', 'Iqlab'], correct: 1, explanation: 'Dal with sukoon mid-verse = Qalqalah Sughra. A minor bounce.', rule: 'qalqalah' },
  { arabicText: 'الْفَلَقِ', question: 'The qaf at the end of this word — which rule?', options: ['Ikhfa', 'Idgham', 'Qalqalah', 'Ghunnah'], correct: 2, explanation: 'Qaf (ق) is one of the 5 qalqalah letters (قُطْبُ جَدّ).', rule: 'qalqalah' },
  { arabicText: 'اقْتَرَبَ', question: 'The qaf with sukoon in اقْتَرَبَ?', options: ['Qalqalah Sughra', 'Idgham', 'Madd', 'Ghunnah'], correct: 0, explanation: 'Qaf with sukoon mid-word = Qalqalah Sughra. Bounce lightly, don\'t add a vowel.', rule: 'qalqalah' },
  { arabicText: 'خَلَقَ', question: 'Is there qalqalah on the qaf in خَلَقَ?', options: ['Yes — Qalqalah', 'No — qaf has a vowel'], correct: 1, explanation: 'No qalqalah here! The qaf has a fatha (vowel). Qalqalah only applies when the letter has sukoon.', rule: 'qalqalah' },

  // ── Mixed / tricky ──
  { arabicText: 'مِنْ حَكِيمٍ', question: 'Noon sakinah before ha — which rule?', options: ['Ikhfa', 'Idgham', 'Izhar', 'Iqlab'], correct: 2, explanation: 'Ha (ح) is a throat letter → Izhar (clear pronunciation). The noon is pronounced clearly.', rule: 'mixed' },
  { arabicText: 'مِنْ عِلْمٍ', question: 'Noon sakinah before ayn — which rule?', options: ['Idgham', 'Izhar', 'Ikhfa', 'Ghunnah'], correct: 1, explanation: 'Ayn (ع) is a throat letter → Izhar. Pronounce the noon clearly before it.', rule: 'mixed' },
];

function shuffleAndPick<T>(arr: T[], count: number): T[] {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
}

const QuizSection: React.FC = () => {
  const [questions, setQuestions] = useState(() => shuffleAndPick(ALL_QUIZ_QUESTIONS, QUESTIONS_PER_ROUND));
  const [currentQ, setCurrentQ] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [roundNumber, setRoundNumber] = useState(1);

  const q = questions[currentQ];
  const isCorrect = selected === q.correct;

  const handleSelect = (idx: number) => {
    if (selected !== null) return;
    setSelected(idx);
    if (idx === q.correct) setScore((s) => s + 1);
  };

  const handleNext = () => {
    if (currentQ + 1 >= questions.length) {
      setFinished(true);
    } else {
      setCurrentQ((c) => c + 1);
      setSelected(null);
    }
  };

  const handleRestart = () => {
    setQuestions(shuffleAndPick(ALL_QUIZ_QUESTIONS, QUESTIONS_PER_ROUND));
    setCurrentQ(0);
    setSelected(null);
    setScore(0);
    setFinished(false);
    setRoundNumber((r) => r + 1);
  };

  if (finished) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="card text-center py-8">
        <div className="text-5xl mb-4">{score >= 4 ? '🌟' : score >= 2 ? '👍' : '📚'}</div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {score} / {questions.length}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
          {score >= 4 ? 'Excellent! You know your tajweed rules well.' : score >= 2 ? 'Good effort! Review the rules you missed above.' : 'Keep studying — review the rules above and try again!'}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-600 mb-5">
          Round {roundNumber} · {ALL_QUIZ_QUESTIONS.length} questions in the bank · {pct}% correct
        </p>
        <button onClick={handleRestart} className="btn-primary text-sm px-6 py-2">
          New Round ({QUESTIONS_PER_ROUND} new questions)
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Quick Quiz</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400 dark:text-gray-600">Round {roundNumber}</span>
          <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-2.5 py-1">
            {currentQ + 1} / {questions.length}
          </span>
        </div>
      </div>

      <p className="font-arabic text-3xl text-center text-gray-900 dark:text-white mb-2 py-3" dir="rtl">{q.arabicText}</p>
      <p className="text-sm text-gray-700 dark:text-gray-300 text-center mb-5 font-medium">{q.question}</p>

      <div className="grid grid-cols-2 gap-2 mb-4">
        {q.options.map((opt, i) => (
          <button
            key={i}
            onClick={() => handleSelect(i)}
            disabled={selected !== null}
            className={clsx(
              'px-4 py-3 rounded-xl text-sm font-medium transition-all border',
              selected === null
                ? 'border-gray-200 dark:border-gray-700 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 text-gray-700 dark:text-gray-300'
                : i === q.correct
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 ring-2 ring-emerald-500/30'
                : i === selected
                ? 'border-red-400 bg-red-50 dark:bg-red-500/15 text-red-600 dark:text-red-400'
                : 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 opacity-60'
            )}
          >
            {opt}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className={clsx(
              'rounded-xl px-4 py-3 mb-4 text-sm',
              isCorrect
                ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300'
            )}
          >
            <span className="font-medium">{isCorrect ? 'Correct! ' : 'Not quite. '}</span>
            {q.explanation}
          </motion.div>
        )}
      </AnimatePresence>

      {selected !== null && (
        <div className="flex justify-end">
          <button onClick={handleNext} className="btn-primary text-sm px-5 py-2">
            {currentQ + 1 >= questions.length ? 'See Results' : 'Next Question'}
          </button>
        </div>
      )}
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────

export default function LearnPage() {
  const [expandedRule, setExpandedRule] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'rules' | 'quiz'>('rules');

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Learn Tajweed Rules
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-5">
          Master the 6 core rules with real Quranic audio examples. Tap each rule to explore, then test yourself with the quiz.
        </p>

        {/* Tab switcher */}
        <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 w-fit">
          <button
            onClick={() => setActiveTab('rules')}
            className={clsx(
              'px-5 py-2 rounded-lg text-sm font-medium transition-all',
              activeTab === 'rules'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            )}
          >
            Rules & Audio
          </button>
          <button
            onClick={() => setActiveTab('quiz')}
            className={clsx(
              'px-5 py-2 rounded-lg text-sm font-medium transition-all',
              activeTab === 'quiz'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            )}
          >
            Quiz
          </button>
        </div>
      </motion.div>

      <AnimatePresence mode="wait">
        {activeTab === 'rules' ? (
          <motion.div
            key="rules"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.2 }}
            className="space-y-4"
          >
            {RULES.map((rule, idx) => {
              const isExpanded = expandedRule === rule.id;

              return (
                <motion.div
                  key={rule.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.04 }}
                >
                  <div
                    className={clsx(
                      'rounded-2xl border overflow-hidden transition-all',
                      rule.borderColor,
                      rule.bgColor,
                      isExpanded && `ring-2 ${rule.ringColor}`
                    )}
                  >
                    {/* Header — always visible */}
                    <button
                      onClick={() => setExpandedRule(isExpanded ? null : rule.id)}
                      className="w-full text-left p-5 sm:p-6"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="flex flex-col items-center gap-0.5">
                            <span className={clsx('text-3xl font-arabic font-bold leading-none', rule.color)}>
                              {rule.arabic}
                            </span>
                            <span className="text-lg">{rule.emoji}</span>
                          </div>
                          <div>
                            <h3 className={clsx('text-lg font-semibold', rule.color)}>{rule.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{rule.tagline}</p>
                          </div>
                        </div>
                        <motion.svg
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="w-5 h-5 text-gray-400 shrink-0"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </motion.svg>
                      </div>
                    </button>

                    {/* Expanded content */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: 'easeInOut' }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 sm:px-6 pb-6 space-y-5 border-t border-gray-200/30 dark:border-gray-700/30 pt-5">

                            {/* Description */}
                            <div>
                              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{rule.description}</p>
                            </div>

                            {/* Letters + Timing */}
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="rounded-xl bg-white/50 dark:bg-gray-800/30 p-4">
                                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Letters</h4>
                                <LettersDisplay letters={rule.letters} color={rule.borderColor} />
                              </div>
                              <div className="rounded-xl bg-white/50 dark:bg-gray-800/30 p-4">
                                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Timing</h4>
                                <p className={clsx('text-sm font-medium mb-2', rule.color)}>{rule.beats}</p>
                                {rule.beats.includes('2') && (
                                  <TimingVisual label="Duration" beats={2} color={rule.bgColor} />
                                )}
                              </div>
                            </div>

                            {/* How to apply */}
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">How to Apply</h4>
                              <ul className="space-y-1.5">
                                {rule.howToApply.map((step, i) => (
                                  <li key={i} className="flex gap-2 text-sm text-gray-600 dark:text-gray-300">
                                    <span className={clsx('shrink-0 mt-0.5', rule.colorMuted)}>
                                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                    </span>
                                    {step}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Listen for / Common mistake */}
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="rounded-xl bg-emerald-50/50 dark:bg-emerald-500/5 border border-emerald-200/50 dark:border-emerald-500/10 p-4">
                                <h4 className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1.5">What to Listen For</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300">{rule.whatToListenFor}</p>
                              </div>
                              <div className="rounded-xl bg-red-50/50 dark:bg-red-500/5 border border-red-200/50 dark:border-red-500/10 p-4">
                                <h4 className="text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wider mb-1.5">Common Mistake</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-300">{rule.commonMistake}</p>
                              </div>
                            </div>

                            {/* Audio examples */}
                            <div>
                              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                                Audio Examples — Listen to Al-Husary
                              </h4>
                              <div className="space-y-3">
                                {rule.examples.map((ex, i) => (
                                  <AyahAudioPlayer
                                    key={i}
                                    surah={ex.surah}
                                    ayah={ex.ayah}
                                    surahName={ex.surahName}
                                    arabicText={ex.arabicText}
                                    highlightIndices={ex.highlightIndices}
                                    explanation={ex.explanation}
                                    ruleColor={rule.color}
                                    ruleColorLight={rule.colorLight}
                                  />
                                ))}
                              </div>
                            </div>

                            {/* Measurement note */}
                            <div className="rounded-xl bg-gray-100/70 dark:bg-gray-800/30 p-3 flex items-start gap-2">
                              <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 00.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M19 14.5l-2.47-2.47M5 14.5l2.47-2.47" />
                              </svg>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                <span className="font-medium">How we measure:</span> {rule.measurementLabel}
                              </p>
                            </div>

                            {/* Practice CTA */}
                            <div className="text-center pt-2">
                              <a
                                href="/practice"
                                className={clsx('inline-flex items-center gap-2 btn-primary text-sm px-6 py-2.5')}
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4M12 15a3 3 0 003-3V5a3 3 0 00-6 0v7a3 3 0 003 3z" />
                                </svg>
                                Practice This Rule
                              </a>
                            </div>

                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            key="quiz"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <QuizSection />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Disclaimer */}
      <div className="mt-10 text-center text-xs text-gray-400 dark:text-gray-600">
        <p>This guide is for educational purposes. For complete tajweed mastery, study with a qualified teacher.</p>
      </div>
    </div>
  );
}
